import React, { useRef, useState } from "react";
import { useAuth, authService } from "auth";
import { AuthFormField } from "components/auth-form/AuthForm";
import { apiErrorMessage } from "utils/errors/parseApiError";
import {
  SIGNED_OUT_MESSAGE,
  attemptsLeftMessage,
  classifyReauthFailure,
  clearReauthAttempts,
  spendReauthAttempt,
} from "utils/reauth";

const MIN_PASSWORD_LENGTH = 8;

export interface UseUpdatePasswordReturn {
  fields: AuthFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  showSuccess: boolean;
  successMsg: string;
  handleSubmit: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
}

export function useUpdatePassword(): UseUpdatePasswordReturn {
  const { signOut } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  /**
   * Whether closing the current modal ends the session. Set on the success path, and on the last
   * allowed wrong password.
   *
   * Both endings sign out on CLOSE rather than immediately: AuthContext's auth listener runs
   * `if (didInit && !user) window.location.replace("/")`, so signing out first would replace the
   * page before either message could be read.
   */
  const signOutOnCloseRef = useRef(false);

  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const endSession = async () => {
    // AuthContext's signOut, not authService's: it revokes the server session through signOutApi
    // and clears the react-query cache, the in-memory app token / session key and the idle clock.
    // The provider's own would leave the __session cookie valid server-side for up to an hour.
    await signOut();
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
    if (signOutOnCloseRef.current) {
      signOutOnCloseRef.current = false;
      void endSession();
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
    if (signOutOnCloseRef.current) {
      signOutOnCloseRef.current = false;
      void endSession();
    }
  };

  const handleSubmit = async () => {
    // The lockout is terminal. The error modal covers the form but traps neither focus nor
    // keystrokes, so without this the user can type past it and carry on.
    if (signOutOnCloseRef.current) return;
    if (!currentPassword) {
      return openError("Please enter your current password.");
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      return openError(
        `Your new password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      );
    }
    if (newPassword !== confirmPassword) {
      return openError("Those passwords don't match. Please try again.");
    }
    if (newPassword === currentPassword) {
      return openError("Your new password must be different from the current one.");
    }

    // ── Leg 1: prove the current password. Only a wrong one spends an attempt. ──
    try {
      setLoadingMsg("Checking your current password…");
      setShowLoading(true);
      await authService.reauthenticate(currentPassword);
      clearReauthAttempts();
    } catch (err: any) {
      const { message, isWrongPassword } = classifyReauthFailure(
        err,
        "We couldn't verify your current password. Please try again.",
      );

      if (!isWrongPassword) {
        // Throttled, offline, or something else that is not a failed guess.
        return openError(message);
      }

      // The shared count, not a per-mount one: leaving the page and coming back must not hand
      // out a fresh three, and the notification-email gate spends the same budget.
      const left = spendReauthAttempt();
      setCurrentPassword("");

      if (left <= 0) {
        signOutOnCloseRef.current = true;
        return openError(SIGNED_OUT_MESSAGE);
      }

      return openError(attemptsLeftMessage(message, left));
    }

    // ── Leg 2: the update. Reauthentication just cleared requires-recent-login. ──
    try {
      setLoadingMsg("Updating your password…");
      setShowLoading(true);
      await authService.changePassword(newPassword);

      setShowLoading(false);
      setLoadingMsg("");
      signOutOnCloseRef.current = true;
      setSuccessMsg(
        "Your password has been updated. Please sign in again with your new password.",
      );
      setShowSuccess(true);
    } catch (err: any) {
      openError(
        apiErrorMessage(err, "Failed to update your password. Please try again."),
      );
    }
  };

  const focusNext =
    (ref: React.RefObject<HTMLInputElement>) => (e: React.KeyboardEvent) => {
      if (e.key === "Enter") ref.current?.focus();
    };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const fields: AuthFormField[] = [
    {
      name: "currentPassword",
      label: "Current Password",
      type: "password",
      value: currentPassword,
      onChange: setCurrentPassword,
      onKeyDown: focusNext(newPasswordRef),
      placeholder: "Enter your current password",
      autoComplete: "current-password",
    },
    {
      name: "newPassword",
      label: "New Password",
      type: "password",
      value: newPassword,
      onChange: setNewPassword,
      onKeyDown: focusNext(confirmRef),
      placeholder: "Enter your new password",
      autoComplete: "new-password",
      inputRef: newPasswordRef,
    },
    {
      name: "confirmPassword",
      label: "Confirm New Password",
      type: "password",
      value: confirmPassword,
      onChange: setConfirmPassword,
      onKeyDown: submitOnEnter,
      placeholder: "Confirm your new password",
      autoComplete: "new-password",
      inputRef: confirmRef,
    },
  ];

  return {
    fields,
    submitRef,
    isSubmitDisabled: !currentPassword || !newPassword || !confirmPassword,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    showSuccess,
    successMsg,
    handleSubmit,
    handleCloseError,
    handleCloseSuccess,
  };
}
